<template>
  <!-- If we have dual media, our UI will fork and render a relatively different layout which is represented below -->
  <PreviewSheet
    v-if="isDualMedia"
    class="content-preview content-preview--cover"
    border="primary"
    radius="0"
  >
    <PreviewSheet
      class="content-preview__content-sheet"
      radius="0"
      :background="hasBackgroundColor ? 'transparent' : undefined"
    >
      <div
        class="content-preview__cover-background"
        :style="{
          backgroundImage: `url(${coverImage})`,
        }"
      ></div>

      <div class="content-preview__layout content-preview__layout--cover">
        <PreviewSheet
          :class="[
            'content-preview__cover-dual-media-sheet flex items-stretch overflow-hidden',
            currentBlockParams.alignment !== 'right'
              ? 'flex-row'
              : 'flex-row-reverse',
          ]"
          background="primary"
        >
          <div class="flex items-center justify-center w-1/2 flex-shrink-0">
            <div
              v-if="foregroundMedia?.type === 'video'"
              :class="[
                'w-full h-full px-8 md:pl-8 flex items-center justify-center',
                currentBlockParams.alignment === 'right' ? 'px-8 md:pr-8' : '',
              ]"
            >
              <iframe
                class="content-preview__video--dual"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                :src="`https://www.youtube.com/embed/${foregroundMedia.params.params.id}`"
              ></iframe>
            </div>

            <PreviewSheet
              v-if="foregroundMedia?.type === 'image'"
              class="content-preview__image--dual w-full h-full"
              radius="0"
              :style="{
                backgroundImage: `url(${foregroundMedia.params.image})`,
              }"
            />
          </div>

          <div class="flex items-center w-1/2">
            <div
              :class="[
                'content-preview__typography-wrapper p-20',
                layout === 'cover' &&
                  'content-preview__typography-wrapper--cover',
                layout === 'generic' &&
                  'content-preview__typography-wrapper--generic',
                'flex-col gap-8 justify-center',
              ]"
            >
              <PreviewTypography
                v-if="currentBlockParams.header"
                :text="currentBlockParams.header"
                variant="heading3"
                :color="textColor"
              />
              <PreviewTypography
                v-if="currentBlockParams.body"
                class="content-preview__body"
                :text="currentBlockParams.body"
                variant="titleSmall"
                :color="textColorSecondary"
              />
              <PreviewButton
                v-if="
                  !!currentBlockParams.action &&
                  currentBlockParams.action.params.label
                "
                :title="currentBlockParams.action.params.label"
                caret
              />
            </div>
          </div>
        </PreviewSheet>
      </div>
    </PreviewSheet>
  </PreviewSheet>

  <PreviewSheet
    v-if="!isDualMedia"
    class="content-preview"
    :class="[
      layout === 'generic' && 'content-preview--generic',
      layout === 'cover' && 'content-preview--cover',
    ]"
    border="primary"
    radius="0"
    :background="hasBackgroundColor ? 'transparent' : undefined"
    :style="containerStyle"
  >
    <PreviewSheet
      class="content-preview__content-sheet"
      :radius="layout === 'cover' ? undefined : '0'"
      :background="hasBackgroundColor ? 'transparent' : undefined"
    >
      <div
        :class="[
          'content-preview__layout',
          layout === 'generic' && 'content-preview__layout--generic',
          layout === 'cover' && 'content-preview__layout--cover',
          currentBlockParams.alignment !== 'right'
            ? 'flex-row'
            : 'flex-row-reverse',
          `justify-${justify}`,
          `align-${layout === 'generic' ? 'center' : 'start'}`,
        ]"
      >
        <div
          v-if="coverImage && layout === 'cover'"
          class="content-preview__cover-background"
          :style="{
            backgroundImage: `url(${coverImage})`,
          }"
        ></div>
        <PreviewSheet
          v-if="layout === 'generic' && foregroundMedia?.type === 'image'"
          class="content-preview__image"
          :style="{
            backgroundImage: `url(${foregroundMedia.params.image})`,
          }"
        />
        <div
          v-if="layout === 'generic' && foregroundMedia?.type === 'video'"
          class="content-preview__video-wrapper"
        >
          <iframe
            class="content-preview__video"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            :src="`https://www.youtube.com/embed/${foregroundMedia.params.params.id}`"
          ></iframe>
        </div>

        <PreviewSheet
          :class="[layout === 'cover' && 'content-preview__cover-stack-sheet']"
          :background="
            hasBackgroundColor
              ? 'transparent'
              : layout === 'cover'
                ? 'primary80'
                : undefined
          "
        >
          <div
            :class="[
              'content-preview__typography-wrapper',
              layout === 'cover' &&
                'content-preview__typography-wrapper--cover',
              layout === 'generic' &&
                'content-preview__typography-wrapper--generic',
              'flex-col gap-8 justify-center',
            ]"
          >
            <PreviewTypography
              v-if="currentBlockParams.header"
              :text="currentBlockParams.header"
              variant="heading3"
              :color="textColor"
            />
            <PreviewTypography
              v-if="currentBlockParams.body"
              class="content-preview__body"
              :text="currentBlockParams.body"
              variant="titleSmall"
              :color="textColorSecondary"
            />
            <PreviewButton
              v-if="
                !!currentBlockParams.action &&
                currentBlockParams.action.params.label
              "
              :title="currentBlockParams.action.params.label"
              caret
            />
          </div>
        </PreviewSheet>
      </div>
    </PreviewSheet>
  </PreviewSheet>
</template>
<script lang="ts" setup>
import { computed } from 'vue'

import PreviewButton from '@/lib/builder/public/preview/PreviewButton.vue'
import PreviewSheet from '@/lib/builder/public/preview/PreviewSheet.vue'
import PreviewTypography from '@/lib/builder/public/preview/PreviewTypography.vue'
import { useExtendedMergedTheme } from '@/lib/theme/useExtendedMergedTheme'
import { tokenToCss, pickTextColor } from '@/utils/backgroundColors'

import type { ContentSection } from '../../../WebsiteBuilder.types'

const props = defineProps<{
  content: ContentSection
}>()

const currentBlockParams = computed(() => {
  return props.content?.params
})

// prefer checking foreground first, then media for backwards compatibility with older section data
const foregroundMedia = computed(() => {
  return props.content?.params.foreground ?? props.content?.params.media
})

const hasBackgroundPhoto = computed(() => {
  const bg = currentBlockParams.value.background
  return !!(bg && bg.type === 'image' && bg.params?.image)
})

const layout = computed(() => {
  if (hasBackgroundPhoto.value === true) return 'cover'

  if (currentBlockParams.value.layout === 'cover') {
    const media = foregroundMedia.value?.params
    return media && 'image' in media ? 'cover' : 'generic'
  }
  return 'generic'
})

const justify = computed(() => {
  if (currentBlockParams.value.alignment === 'center') {
    return 'center'
  } else if (currentBlockParams.value.alignment === 'right') {
    return 'between'
  } else {
    return 'start'
  }
})

const coverImage = computed(() => {
  if (hasBackgroundPhoto.value) {
    const bg = currentBlockParams.value.background
    return bg?.type === 'image' ? bg.params.image : null
  }
  const media = foregroundMedia.value?.params
  return media && 'image' in media ? media.image : null
})

const isDualMedia = computed(() => {
  return (
    // specifically check for foreground type, not media type
    // so we only render isDualMedia for migrated content sections
    currentBlockParams.value.foreground?.type &&
    currentBlockParams.value.background?.type === 'image'
  )
})

// Background color and text color resolution
const backgroundToken = computed(() =>
  currentBlockParams.value.background?.type === 'color'
    ? currentBlockParams.value.background.params.token
    : null
)

const hasBackgroundColor = computed(() => !!backgroundToken.value)

const theme = useExtendedMergedTheme()

const containerStyle = computed(() => {
  if (!backgroundToken.value) return undefined
  const css = tokenToCss(backgroundToken.value, theme.value)
  return css ? { backgroundColor: css } : undefined
})

const textColor = computed(() => pickTextColor(backgroundToken.value))
const textColorSecondary = computed(() =>
  pickTextColor(backgroundToken.value, true)
)
</script>

<style lang="scss" scoped>
.content-preview {
  &--cover {
    padding: 8px;
  }

  &__content-sheet {
    overflow: hidden;
  }

  &__layout {
    gap: 126px;
    position: relative;

    &--cover {
      padding: 80px;
    }

    &--generic {
      padding: 120px;
    }
  }

  &__cover-background {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background-size: cover;
    background-position: center center;
  }

  &__cover-stack-sheet {
    padding: 80px;
    z-index: 10;
    min-height: 640px;
    display: flex;
    backdrop-filter: blur(56px);
  }

  &__cover-dual-media-sheet {
    z-index: 10;
    min-height: 640px;
    display: flex;
  }

  &__image {
    width: 520px;
    height: 520px;
    flex-shrink: 0;

    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;

    &--dual {
      width: 100%;
      min-height: 640px;
      background-position: center center;
      background-repeat: no-repeat;
      background-size: cover;
    }
  }

  &__video-wrapper {
    position: relative;
    aspect-ratio: 16 / 9;

    &--dual {
      position: relative;
      width: 100%;
      height: 100%;
    }
  }

  &__video {
    position: relative;
    width: 540px;
    height: 303px;
    border: 0;

    &--dual {
      min-height: 300px;
      width: 100%;
    }
  }

  &__typography-wrapper {
    max-width: 720px;

    &--cover {
      position: relative;
      z-index: 20;
      max-width: 560px;
    }
  }
}
</style>
